import { getUiScale, UI_POPUP_ROOT_ID } from './uiScale'

const POSITION_PROPS = [
  'top',
  'left',
  'right',
  'bottom',
  'insetInlineStart',
  'insetInlineEnd',
  'marginTop',
] as const

type PositionProp = (typeof POSITION_PROPS)[number]
type PositionSnapshot = Record<PositionProp, string>

const PX_VALUE_RE = /^\s*(-?\d+(?:\.\d+)?)px\s*$/i
let lastInteractionTarget: HTMLElement | null = null

function isPopupOverlayElement(node: Node, popupRoot: HTMLElement): node is HTMLElement {
  return node instanceof HTMLElement && node.parentElement === popupRoot
}

function toCompensatedPixelValue(
  value: string,
  scale: number,
  prop: PositionProp,
): string {
  const match = value.match(PX_VALUE_RE)
  if (!match) return value

  const px = Number.parseFloat(match[1] ?? '')
  if (!Number.isFinite(px)) return value

  if (prop === 'marginTop') {
    // Keep PrimeVue's computed anchor gutter unscaled to reduce visible gap.
    return value
  }

  return `${(px / scale).toFixed(3).replace(/\.?0+$/, '')}px`
}

function parsePx(value: string): number | null {
  const match = value.match(PX_VALUE_RE)
  if (!match) return null
  const px = Number.parseFloat(match[1] ?? '')
  return Number.isFinite(px) ? px : null
}

function findAnchorElement(overlay: HTMLElement): HTMLElement | null {
  if (!overlay.id) return null
  const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(overlay.id) : overlay.id
  const anchor = document.querySelector<HTMLElement>(`[aria-controls="${escapedId}"]`)
  if (anchor) return anchor
  const listAnchor = document.querySelector<HTMLElement>(`[aria-controls="${escapedId}_list"]`)
  return listAnchor ?? lastInteractionTarget
}

function rememberInteractionTarget(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLElement)) return

  const candidate = target.closest<HTMLElement>(
    '[aria-haspopup], [role="combobox"], input, button, [data-pc-section="trigger"]',
  )
  if (candidate) lastInteractionTarget = candidate
}

function adjustInlinePositionByDelta(overlay: HTMLElement, deltaVisualPx: number, scale: number): void {
  if (Math.abs(deltaVisualPx) < 0.5) return
  const deltaStylePx = deltaVisualPx / scale
  const left = parsePx(overlay.style.left)
  const insetStart = parsePx(overlay.style.insetInlineStart)
  const right = parsePx(overlay.style.right)
  const insetEnd = parsePx(overlay.style.insetInlineEnd)

  if (left != null) {
    overlay.style.left = `${(left + deltaStylePx).toFixed(3).replace(/\.?0+$/, '')}px`
    return
  }

  if (insetStart != null) {
    overlay.style.insetInlineStart = `${(insetStart + deltaStylePx).toFixed(3).replace(/\.?0+$/, '')}px`
    return
  }

  if (right != null) {
    overlay.style.right = `${(right - deltaStylePx).toFixed(3).replace(/\.?0+$/, '')}px`
    return
  }

  if (insetEnd != null) {
    overlay.style.insetInlineEnd = `${(insetEnd - deltaStylePx).toFixed(3).replace(/\.?0+$/, '')}px`
  }
}

function adjustBlockPositionByDelta(overlay: HTMLElement, deltaVisualPx: number, scale: number): void {
  if (Math.abs(deltaVisualPx) < 0.5) return
  const deltaStylePx = deltaVisualPx / scale
  const top = parsePx(overlay.style.top)
  const bottom = parsePx(overlay.style.bottom)

  if (top != null) {
    overlay.style.top = `${(top + deltaStylePx).toFixed(3).replace(/\.?0+$/, '')}px`
    return
  }

  if (bottom != null) {
    overlay.style.bottom = `${(bottom - deltaStylePx).toFixed(3).replace(/\.?0+$/, '')}px`
  }
}

function refineOverlayAnchorAlignment(overlay: HTMLElement, scale: number): void {
  const anchor = findAnchorElement(overlay)
  if (!anchor) return

  const overlayRect = overlay.getBoundingClientRect()
  const anchorRect = anchor.getBoundingClientRect()
  if (overlayRect.width <= 0) return

  const viewportWidth = window.innerWidth
  const preferredLeft = anchorRect.left
  const flippedLeft = anchorRect.right - overlayRect.width

  let desiredLeft = preferredLeft
  if (preferredLeft + overlayRect.width > viewportWidth) {
    desiredLeft = flippedLeft >= 0
      ? flippedLeft
      : Math.max(0, viewportWidth - overlayRect.width)
  }

  const deltaX = desiredLeft - overlayRect.left
  adjustInlinePositionByDelta(overlay, deltaX, scale)

  const alignAbove =
    Math.abs(overlayRect.bottom - anchorRect.top) < Math.abs(overlayRect.top - anchorRect.bottom)
  const desiredTop = alignAbove ? anchorRect.top - overlayRect.height : anchorRect.bottom
  const deltaY = desiredTop - overlayRect.top
  adjustBlockPositionByDelta(overlay, deltaY, scale)
}

function readPositionSnapshot(element: HTMLElement): PositionSnapshot {
  return {
    top: element.style.top,
    left: element.style.left,
    right: element.style.right,
    bottom: element.style.bottom,
    insetInlineStart: element.style.insetInlineStart,
    insetInlineEnd: element.style.insetInlineEnd,
    marginTop: element.style.marginTop,
  }
}

function applyPositionSnapshot(element: HTMLElement, snapshot: PositionSnapshot, scale: number): void {
  for (const prop of POSITION_PROPS) {
    const rawValue = snapshot[prop]
    const nextValue = scale === 1 ? rawValue : toCompensatedPixelValue(rawValue, scale, prop)
    ;(element.style as CSSStyleDeclaration)[prop] = nextValue
  }

  if (scale !== 1) {
    refineOverlayAnchorAlignment(element, scale)
  }
}

export function initOverlayPositionSync(): () => void {
  const popupRoot = document.getElementById(UI_POPUP_ROOT_ID)
  if (!popupRoot) return () => {}

  const rawPositionMap = new WeakMap<HTMLElement, PositionSnapshot>()
  const isApplying = new WeakSet<HTMLElement>()

  const syncElement = (element: HTMLElement) => {
    const snapshot = rawPositionMap.get(element) ?? readPositionSnapshot(element)
    rawPositionMap.set(element, snapshot)

    const scale = getUiScale()
    isApplying.add(element)
    applyPositionSnapshot(element, snapshot, scale)
    queueMicrotask(() => isApplying.delete(element))
  }

  const syncAll = () => {
    popupRoot.childNodes.forEach((node) => {
      if (node instanceof HTMLElement) syncElement(node)
    })
  }

  const overlayObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (!isPopupOverlayElement(node, popupRoot)) return
          rawPositionMap.set(node, readPositionSnapshot(node))
          syncElement(node)
        })
        continue
      }

      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        const target = mutation.target
        if (!isPopupOverlayElement(target, popupRoot)) continue
        if (isApplying.has(target)) continue
        rawPositionMap.set(target, readPositionSnapshot(target))
        syncElement(target)
      }
    }
  })

  const scaleObserver = new MutationObserver(syncAll)

  overlayObserver.observe(popupRoot, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  })

  scaleObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style', 'data-ui-scale-applied'],
  })

  document.addEventListener('pointerdown', rememberInteractionTarget, true)
  document.addEventListener('focusin', rememberInteractionTarget, true)

  syncAll()

  return () => {
    overlayObserver.disconnect()
    scaleObserver.disconnect()
    document.removeEventListener('pointerdown', rememberInteractionTarget, true)
    document.removeEventListener('focusin', rememberInteractionTarget, true)
    lastInteractionTarget = null
  }
}
